import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { businessDaysSince, businessDayLabel, casinoWallTime } from '../utils/casinoDay';
import {
  Search,
  Filter,
  MoreVertical,
  Ban,
  Unlock,
  Key,
  Trash2,
  Shield,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  UserPlus,
  Copy,
  Check,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  Star,
  Clock,
  Gift,
  MapPin,
  Gamepad2,
  Award,
  TrendingUp,
  Link2,
  Link2Off,
  RefreshCw,
  Cake,
  AlertCircle,
  FileText,
  Crown,
  Zap,
  Lock,
} from 'lucide-react';
import PlayerDetailModal from '../components/PlayerDetailModal';

export default function Users() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [linkedFilter, setLinkedFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [sort, setSort] = useState({ by: 'createdAt', order: 'DESC' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', firstName: '', lastName: '', phone: '', role: 'user' });
  const [addLoading, setAddLoading] = useState(false);
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [copied, setCopied] = useState(false);

  // Password reset state
  const [passwordResetMode, setPasswordResetMode] = useState('random'); // 'random' or 'custom'
  const [customPassword, setCustomPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState(null);

  // Role change state
  const [newRole, setNewRole] = useState('');

  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Player stats panel state
  const [showStatsPanel, setShowStatsPanel] = useState(false);
  const [statsPanelUser, setStatsPanelUser] = useState(null);

  // Handle URL filter parameters from Dashboard links
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');

    if (filterParam) {
      switch (filterParam) {
        case 'banned':
          setStatusFilter('banned');
          break;
        case 'new':
          // For "new this month", we can't easily filter by date client-side
          // Just navigate to users page - could add date filter later
          break;
        case 'linked':
          setLinkedFilter('linked');
          break;
        case 'staff':
          setRoleFilter('staff');
          break;
        default:
          break;
      }
      // Clear the URL param after applying filter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter }),
        sortBy: sort.by,
        sortOrder: sort.order,
      });
      const response = await api.get(`/admin/users?${params}`);
      let filteredUsers = response.data.users;

      // Apply linked filter client-side (since backend doesn't support it)
      if (linkedFilter === 'linked') {
        filteredUsers = filteredUsers.filter(u => u.playerClubMember?.memberNumber);
      } else if (linkedFilter === 'unlinked') {
        filteredUsers = filteredUsers.filter(u => !u.playerClubMember?.memberNumber);
      }

      setUsers(filteredUsers);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, roleFilter, statusFilter, linkedFilter, sort.by, sort.order]);

  // Click a sortable header: same column flips direction, new column starts
  // DESC (most recent / highest first, which is what you almost always want).
  const toggleSort = (column) => {
    setSort((prev) => (prev.by === column
      ? { by: column, order: prev.order === 'DESC' ? 'ASC' : 'DESC' }
      : { by: column, order: 'DESC' }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Last visit is measured in BUSINESS days (gaming day runs 10:00 -> 06:00, so a
  // 1am session still belongs to last night). See utils/casinoDay.js for why these
  // timestamps must never be parsed as UTC.
  const formatLastVisit = (value) => {
    if (!value) return { text: 'Never', className: 'text-gray-600' };
    const days = businessDaysSince(value);
    const className = days <= 7 ? 'text-green-400'
      : days <= 30 ? 'text-gray-300'
      : days <= 90 ? 'text-amber-500'
      : 'text-red-400';
    return { text: businessDayLabel(value), className, title: casinoWallTime(value) };
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleAction = async (action) => {
    if (!selectedUser) return;
    setActionLoading(true);

    try {
      switch (action) {
        case 'ban':
          await api.put(`/admin/users/${selectedUser.id}`, { isActive: false });
          break;
        case 'unban':
          await api.put(`/admin/users/${selectedUser.id}`, { isActive: true });
          break;
        case 'resetPassword':
          const passwordPayload = passwordResetMode === 'custom' && customPassword
            ? { newPassword: customPassword }
            : {};
          const pwResult = await api.post(`/admin/users/${selectedUser.id}/reset-password`, passwordPayload);
          if (passwordResetMode === 'custom') {
            setGeneratedPassword(customPassword);
          } else {
            setGeneratedPassword(pwResult.data.temporaryPassword);
          }
          // Don't close modal yet - show the generated password
          setActionLoading(false);
          return;
        case 'changeRole':
          await api.post(`/admin/admins/${selectedUser.id}/promote`, { role: newRole });
          break;
        case 'editEmail': {
          const trimmed = newEmail.trim().toLowerCase();
          if (!trimmed) {
            setEmailError('Email is required');
            setActionLoading(false);
            return;
          }
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(trimmed)) {
            setEmailError('Invalid email format');
            setActionLoading(false);
            return;
          }
          if (trimmed === selectedUser.email?.toLowerCase()) {
            setEmailError('New email matches the current email');
            setActionLoading(false);
            return;
          }
          try {
            await api.put(`/admin/users/${selectedUser.id}/email`, { email: trimmed });
          } catch (err) {
            setEmailError(err.response?.data?.error || 'Failed to update email');
            setActionLoading(false);
            return;
          }
          break;
        }
        case 'delete':
          await api.delete(`/admin/users/${selectedUser.id}`);
          break;
      }
      fetchUsers();
      setShowModal(false);
      setSelectedUser(null);
      resetModalState();
    } catch (err) {
      console.error('Error performing action:', err);
      alert(err.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const resetModalState = () => {
    setPasswordResetMode('random');
    setCustomPassword('');
    setGeneratedPassword(null);
    setNewRole('');
    setNewEmail('');
    setEmailError('');
  };

  const openActionModal = (user, action, e) => {
    e.stopPropagation(); // Prevent row click
    setSelectedUser(user);
    setModalAction(action);
    resetModalState();
    if (action === 'changeRole') {
      setNewRole(user.role);
    }
    if (action === 'editEmail') {
      setNewEmail(user.email || '');
    }
    setShowModal(true);
  };

  const handleRowClick = (user) => {
    setStatsPanelUser(user);
    setShowStatsPanel(true);
  };

  const handleAddUser = async () => {
    if (!addForm.email) {
      alert('Email is required');
      return;
    }
    setAddLoading(true);
    try {
      const response = await api.post('/admin/users', addForm);
      setNewUserCredentials({
        email: response.data.user.email,
        password: response.data.temporaryPassword,
        name: `${response.data.user.firstName} ${response.data.user.lastName}`.trim(),
      });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  const handleCopyCredentials = () => {
    if (newUserCredentials) {
      const text = `EDH Casino App Login\nEmail: ${newUserCredentials.email}\nTemporary Password: ${newUserCredentials.password}\n\nPlease download the app and log in with these credentials.`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddForm({ email: '', firstName: '', lastName: '', phone: '', role: 'user' });
    setNewUserCredentials(null);
    setCopied(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">User Management</h1>
        {isAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 font-medium"
          >
            <UserPlus size={18} />
            Add User
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by email, name, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
        >
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="banned">Banned</option>
        </select>
        <select
          value={linkedFilter}
          onChange={(e) => setLinkedFilter(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
        >
          <option value="">All Accounts</option>
          <option value="linked">Linked to Player's Club</option>
          <option value="unlinked">Not Linked</option>
        </select>
      </div>

      {/* Hint */}
      <p className="text-gray-500 text-sm flex items-center gap-2">
        <User size={14} />
        Click on a row to view detailed player information
      </p>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Player's Club</th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer select-none hover:text-amber-500"
                  onClick={() => toggleSort('lastVisit')}
                  title="Most recent seated session"
                >
                  Last Visit
                  <span className="ml-1 text-[10px]">{sort.by === 'lastVisit' ? (sort.order === 'DESC' ? '▼' : '▲') : '↕'}</span>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer select-none hover:text-amber-500"
                  onClick={() => toggleSort('createdAt')}
                >
                  Joined
                  <span className="ml-1 text-[10px]">{sort.by === 'createdAt' ? (sort.order === 'DESC' ? '▼' : '▲') : '↕'}</span>
                </th>
                {isAdmin && <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No users found</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-700/30 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(user)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                          <User size={18} className="text-gray-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.firstName} {user.lastName}</p>
                          <p className="text-sm text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{user.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                        user.role === 'staff' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {user.isActive ? 'Active' : 'Banned'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {user.playerClubMember ? (
                        <span className="text-amber-500 flex items-center gap-1">
                          <CreditCard size={14} />
                          {user.playerClubMember.memberNumber}
                        </span>
                      ) : (
                        <span className="text-gray-500 flex items-center gap-1">
                          <Link2Off size={14} />
                          Not linked
                        </span>
                      )}
                    </td>
                    {(() => {
                      const lv = formatLastVisit(user.lastVisit);
                      return (
                        <td className={`px-4 py-3 text-sm ${lv.className}`} title={lv.title}>
                          {lv.text}
                        </td>
                      );
                    })()}
                    <td className="px-4 py-3 text-gray-500 text-sm">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && (
                        <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={(e) => openActionModal(user, 'changeRole', e)}
                            className="p-2 text-gray-400 hover:text-purple-500 hover:bg-gray-700 rounded-lg"
                            title="Change Role"
                          >
                            <Shield size={16} />
                          </button>
                          <button
                            onClick={(e) => openActionModal(user, 'editEmail', e)}
                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                            title="Change Email"
                          >
                            <Mail size={16} />
                          </button>
                          <button
                            onClick={(e) => openActionModal(user, 'resetPassword', e)}
                            className="p-2 text-gray-400 hover:text-amber-500 hover:bg-gray-700 rounded-lg"
                            title="Reset Password"
                          >
                            <Key size={16} />
                          </button>
                          {user.isActive ? (
                            <button
                              onClick={(e) => openActionModal(user, 'ban', e)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded-lg"
                              title="Ban User"
                            >
                              <Ban size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => openActionModal(user, 'unban', e)}
                              className="p-2 text-gray-400 hover:text-green-500 hover:bg-gray-700 rounded-lg"
                              title="Unban User"
                            >
                              <Unlock size={16} />
                            </button>
                          )}
                          <button
                            onClick={(e) => openActionModal(user, 'delete', e)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded-lg"
                            title="Delete User"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-gray-400 text-sm">Page {pagination.page} of {pagination.totalPages}</span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.totalPages}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Player Stats Panel */}
      <PlayerDetailModal
        visible={showStatsPanel}
        onClose={() => setShowStatsPanel(false)}
        appUserId={statsPanelUser?.id}
        playerClubMember={statsPanelUser?.playerClubMember}
      />

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {modalAction === 'ban' && 'Ban User'}
                {modalAction === 'unban' && 'Unban User'}
                {modalAction === 'resetPassword' && 'Reset Password'}
                {modalAction === 'changeRole' && 'Change User Role'}
                {modalAction === 'editEmail' && 'Change Email Address'}
                {modalAction === 'delete' && 'Delete User'}
              </h3>
              <button onClick={() => { setShowModal(false); resetModalState(); }} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Password Reset - Show result */}
            {modalAction === 'resetPassword' && generatedPassword ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4">
                  <p className="text-green-400 font-medium mb-2">âœ“ Password reset successfully!</p>
                  <p className="text-gray-300 text-sm">Share the new password with the user:</p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 mb-1">Email:</p>
                  <p className="text-white mb-3">{selectedUser?.email}</p>
                  <p className="text-gray-400 mb-1">New Password:</p>
                  <p className="text-amber-500 font-mono text-lg">{generatedPassword}</p>
                </div>

                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`Email: ${selectedUser?.email}\nNew Password: ${generatedPassword}`);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 flex items-center justify-center gap-2"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? 'Copied!' : 'Copy Credentials'}
                </button>

                <button
                  onClick={() => { setShowModal(false); resetModalState(); fetchUsers(); }}
                  className="w-full py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                >
                  Done
                </button>
              </div>
            ) : modalAction === 'resetPassword' ? (
              /* Password Reset - Options */
              <div className="space-y-4">
                <p className="text-gray-400">
                  Reset password for <span className="text-white font-medium">{selectedUser?.email}</span>
                </p>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={passwordResetMode === 'random'}
                      onChange={() => setPasswordResetMode('random')}
                      className="w-4 h-4 text-amber-500"
                    />
                    <div>
                      <p className="text-white font-medium">Generate Random Password</p>
                      <p className="text-gray-400 text-sm">A secure 12-character password will be generated</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={passwordResetMode === 'custom'}
                      onChange={() => setPasswordResetMode('custom')}
                      className="w-4 h-4 text-amber-500"
                    />
                    <div>
                      <p className="text-white font-medium">Set Custom Password</p>
                      <p className="text-gray-400 text-sm">Enter a specific password for the user</p>
                    </div>
                  </label>
                </div>

                {passwordResetMode === 'custom' && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">New Password</label>
                    <input
                      type="text"
                      value={customPassword}
                      onChange={(e) => setCustomPassword(e.target.value)}
                      placeholder="Enter new password..."
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                    {customPassword && customPassword.length < 6 && (
                      <p className="text-red-400 text-xs mt-1">Password should be at least 6 characters</p>
                    )}
                  </div>
                )}

                <p className="text-gray-500 text-sm">
                  The user will be logged out of all sessions.
                </p>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => { setShowModal(false); resetModalState(); }}
                    className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAction('resetPassword')}
                    disabled={actionLoading || (passwordResetMode === 'custom' && customPassword.length < 6)}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black rounded-lg font-medium disabled:opacity-50"
                  >
                    {actionLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </div>
            ) : modalAction === 'changeRole' ? (
              /* Role Change */
              <div className="space-y-4">
                <p className="text-gray-400">
                  Change role for <span className="text-white font-medium">{selectedUser?.email}</span>
                </p>

                <div className="space-y-2">
                  <label className="block text-sm text-gray-400 mb-1">Select Role</label>
                  <div className="space-y-2">
                    {['user', 'staff', 'admin'].map((role) => (
                      <label
                        key={role}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          newRole === role
                            ? 'bg-amber-500/20 border border-amber-500'
                            : 'bg-gray-700 hover:bg-gray-600 border border-transparent'
                        }`}
                      >
                        <input
                          type="radio"
                          name="role"
                          checked={newRole === role}
                          onChange={() => setNewRole(role)}
                          className="w-4 h-4 text-amber-500"
                        />
                        <div className="flex-1">
                          <p className="text-white font-medium capitalize">{role}</p>
                          <p className="text-gray-400 text-sm">
                            {role === 'user' && 'Standard app user with basic access'}
                            {role === 'staff' && 'Can access admin dashboard with limited permissions'}
                            {role === 'admin' && 'Full administrative access to all features'}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                          role === 'staff' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {role}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {newRole !== selectedUser?.role && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-amber-400 text-sm">
                      Role will change from <span className="font-medium">{selectedUser?.role}</span> to <span className="font-medium">{newRole}</span>
                    </p>
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => { setShowModal(false); resetModalState(); }}
                    className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAction('changeRole')}
                    disabled={actionLoading || newRole === selectedUser?.role}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium disabled:opacity-50"
                  >
                    {actionLoading ? 'Updating...' : 'Update Role'}
                  </button>
                </div>
              </div>
            ) : modalAction === 'editEmail' ? (
              /* Email Change */
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Current Email</label>
                  <p className="text-white font-mono text-sm bg-gray-700/50 rounded-lg px-3 py-2">{selectedUser?.email}</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">New Email *</label>
                  <input
                    type="email"
                    autoComplete="off"
                    value={newEmail}
                    onChange={(e) => { setNewEmail(e.target.value); if (emailError) setEmailError(''); }}
                    placeholder="newemail@example.com"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-400"
                  />
                  {emailError && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {emailError}
                    </p>
                  )}
                </div>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-300">
                  <p className="font-medium mb-1 flex items-center gap-1.5">
                    <AlertCircle size={14} /> Heads up
                  </p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-200/90 text-xs">
                    <li>The user will be signed out of all sessions and must log in with the new email.</li>
                    <li>Email-verified status will be reset; the user will need to re-verify.</li>
                  </ul>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => { setShowModal(false); resetModalState(); }}
                    className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAction('editEmail')}
                    disabled={actionLoading || !newEmail.trim() || newEmail.trim().toLowerCase() === (selectedUser?.email || '').toLowerCase()}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50"
                  >
                    {actionLoading ? 'Updating...' : 'Update Email'}
                  </button>
                </div>
              </div>
            ) : (
              /* Ban/Unban/Delete */
              <>
                <p className="text-gray-400 mb-6">
                  {modalAction === 'ban' && `Are you sure you want to ban ${selectedUser?.email}? They will be logged out and unable to access the app.`}
                  {modalAction === 'unban' && `Are you sure you want to unban ${selectedUser?.email}? They will be able to log in again.`}
                  {modalAction === 'delete' && `Are you sure you want to delete ${selectedUser?.email}? This action cannot be undone.`}
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => { setShowModal(false); resetModalState(); }}
                    className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAction(modalAction)}
                    disabled={actionLoading}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      modalAction === 'delete' || modalAction === 'ban'
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-amber-500 hover:bg-amber-600 text-black'
                    } disabled:opacity-50`}
                  >
                    {actionLoading ? 'Processing...' : 'Confirm'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <UserPlus size={20} className="text-amber-500" />
                {newUserCredentials ? 'User Created!' : 'Add New User'}
              </h3>
              <button onClick={closeAddModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {newUserCredentials ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4">
                  <p className="text-green-400 font-medium mb-2">âœ“ User created successfully!</p>
                  <p className="text-gray-300 text-sm">Share these credentials with the user:</p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                  {newUserCredentials.name && (
                    <p className="text-gray-400">Name: <span className="text-white">{newUserCredentials.name}</span></p>
                  )}
                  <p className="text-gray-400">Email: <span className="text-white">{newUserCredentials.email}</span></p>
                  <p className="text-gray-400">Temporary Password: <span className="text-amber-500 font-mono">{newUserCredentials.password}</span></p>
                </div>

                <button
                  onClick={handleCopyCredentials}
                  className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 flex items-center justify-center gap-2"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? 'Copied!' : 'Copy Credentials'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  The user will be prompted to change their password on first login.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email *</label>
                  <input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="user@example.com"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">First Name</label>
                    <input
                      type="text"
                      value={addForm.firstName}
                      onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
                      placeholder="John"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={addForm.lastName}
                      onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
                      placeholder="Doe"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={addForm.phone}
                    onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                    placeholder="(555) 555-5555"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Role</label>
                  <select
                    value={addForm.role}
                    onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="user">User</option>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <button
                  onClick={handleAddUser}
                  disabled={addLoading || !addForm.email}
                  className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {addLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus size={18} />
                      Create User
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  A temporary password will be generated automatically.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
